import { createAdminApiClient } from '../adminApiClient';

/**
 * @typedef {{ id: number, nombre: string, precio: number, incluida: boolean,
 *   obligatoria: boolean, cantidad_incluida?: number, imagen?: string,
 *   imagen_formatted?: string, tallasSelect?: Array<{ id: number, nombre: string }> }} Indumentaria
 * @typedef {{ current_page: number, last_page: number, total: number, per_page: number }} PaginationMeta
 */

/**
 * Construye la URL base para los endpoints de indumentarias de una lista.
 *
 * @param {number|string} listaId
 * @returns {string}
 */
function baseUrl(listaId) {
    return `/listas-abonados/${listaId}/bases/indumentarias`;
}

export default {
    // ── CRUD ────────────────────────────────────────────────────────────────

    /**
     * Obtiene el listado paginado de indumentarias.
     * GET /listas-abonados/{lista_id}/bases/indumentarias
     *
     * @param {{ lista_id: number|string, page?: number, per_page?: number,
     *   filters?: { search?: string, talla?: string } }} params
     * @returns {Promise<{ items: Indumentaria[], meta: PaginationMeta }>}
     */
    async getIndumentarias({ lista_id, page = 1, per_page = 10, filters = {} }) {
        const client = createAdminApiClient();

        const response = await client.get(baseUrl(lista_id), {
            params: {
                page,
                per_page,
                search: filters.search || '',
                talla: filters.talla || '',
            },
        });

        const data = response.data ?? {};
        const rawItems = data.data_indumentarias ?? data.data ?? data.items ?? [];
        const items = Array.isArray(rawItems) ? rawItems : [];
        const meta = {
            current_page: data.current_page ?? data.meta?.current_page ?? 1,
            last_page: data.last_page ?? data.meta?.last_page ?? 1,
            total: data.total ?? data.meta?.total ?? items.length,
            per_page: data.per_page ?? data.meta?.per_page ?? items.length,
        };

        return { items, meta };
    },

    /**
     * Obtiene el detalle de una indumentaria.
     * GET /listas-abonados/{lista_id}/bases/indumentarias/{id}
     *
     * @param {{ lista_id: number|string, indumentaria_id: number|string }} params
     * @returns {Promise<Indumentaria>}
     */
    async getIndumentaria({ lista_id, indumentaria_id }) {
        const client = createAdminApiClient();

        const response = await client.get(
            `${baseUrl(lista_id)}/${indumentaria_id}`
        );

        return response.data?.data ?? response.data ?? {};
    },

    /**
     * Crea una nueva indumentaria.
     * POST /listas-abonados/{lista_id}/bases/indumentarias/crear
     *
     * @param {{ lista_id: number|string, data: Object }} params
     * @returns {Promise<{ data: { message: string } }>}
     */
    async createIndumentaria({ lista_id, data }) {
        const client = createAdminApiClient();

        return client.post(`${baseUrl(lista_id)}/crear`, { ...data, lista_id, filters: {} });
    },

    /**
     * Actualiza una indumentaria existente.
     * PATCH /listas-abonados/{lista_id}/bases/indumentarias/{id}/update
     *
     * @param {{ lista_id: number|string, indumentaria_id: number|string,
     *   data: Object }} params
     * @returns {Promise<{ data: { message: string } }>}
     */
    async updateIndumentaria({ lista_id, indumentaria_id, data }) {
        const client = createAdminApiClient();

        return client.patch(
            `${baseUrl(lista_id)}/${indumentaria_id}/update`,
            { ...data, lista_id, filters: {} }
        );
    },

    /**
     * Elimina una indumentaria.
     * DELETE /listas-abonados/{lista_id}/bases/indumentarias/{id}/delete
     *
     * @param {{ lista_id: number|string, indumentaria_id: number|string }} params
     * @returns {Promise<{ data: { message: string } }>}
     */
    async deleteIndumentaria({ lista_id, indumentaria_id }) {
        const client = createAdminApiClient();

        return client.delete(
            `${baseUrl(lista_id)}/${indumentaria_id}/delete`
        );
    },

    // ── Tallas ──────────────────────────────────────────────────────────────

    /**
     * Obtiene todas las tallas disponibles para la lista.
     * GET /listas-abonados/{lista_id}/bases/indumentarias/tallas
     *
     * @param {{ lista_id: number|string }} params
     * @returns {Promise<Array<{ id: number, nombre: string }>>}
     */
    async getTallas({ lista_id }) {
        const client = createAdminApiClient();

        const response = await client.get(`${baseUrl(lista_id)}/tallas`);

        return response.data?.tallas ?? response.data?.data ?? [];
    },

    /**
     * Obtiene las tallas de una indumentaria específica.
     * GET /listas-abonados/{lista_id}/bases/indumentarias/{id}/tallas
     *
     * @param {{ lista_id: number|string, indumentaria_id: number|string }} params
     * @returns {Promise<Array>}
     */
    async getTallasIndumentaria({ lista_id, indumentaria_id }) {
        const client = createAdminApiClient();

        const response = await client.get(
            `${baseUrl(lista_id)}/${indumentaria_id}/tallas`
        );

        return response.data?.data ?? response.data ?? [];
    },

    /**
     * Obtiene el stock de una talla específica de una indumentaria.
     * GET /listas-abonados/{lista_id}/bases/indumentarias/{id}/tallas/{talla_id}
     *
     * @param {{ lista_id: number|string, indumentaria_id: number|string,
     *   talla_id: number|string }} params
     * @returns {Promise<Object>}
     */
    async getStockTalla({ lista_id, indumentaria_id, talla_id }) {
        const client = createAdminApiClient();

        const response = await client.get(
            `${baseUrl(lista_id)}/${indumentaria_id}/tallas/${talla_id}`
        );

        return response.data?.data ?? response.data ?? {};
    },

    // ── Inventario ──────────────────────────────────────────────────────────

    /**
     * Obtiene las indumentarias recogidas y sin recoger.
     * GET /listas-abonados/{lista_id}/bases/indumentarias/inventario/recogidas
     *
     * @param {{ lista_id: number|string, page?: number, per_page?: number,
     *   filters?: Object }} params
     * @returns {Promise<{ items: Array, meta: PaginationMeta }>}
     */
    async getInventarioRecogidas({ lista_id, page = 1, per_page = 10, filters = {} }) {
        const client = createAdminApiClient();

        const response = await client.get(
            `${baseUrl(lista_id)}/inventario/recogidas`,
            { params: { page, per_page, ...filters } }
        );

        const data = response.data ?? {};
        const rawItems = data.data_indumentarias ?? data.data ?? data.items ?? [];
        const items = Array.isArray(rawItems) ? rawItems : [];
        const meta = {
            current_page: data.current_page ?? data.meta?.current_page ?? 1,
            last_page: data.last_page ?? data.meta?.last_page ?? 1,
            total: data.total ?? data.meta?.total ?? items.length,
            per_page: data.per_page ?? data.meta?.per_page ?? items.length,
        };

        return { items, meta };
    },

    /**
     * Obtiene las indumentarias vendidas por prenda y talla.
     * GET /listas-abonados/{lista_id}/bases/indumentarias/inventario/vendidas
     *
     * @param {{ lista_id: number|string, page?: number, per_page?: number,
     *   filters?: Object }} params
     * @returns {Promise<{ items: Array, meta: PaginationMeta }>}
     */
    async getInventarioVendidas({ lista_id, page = 1, per_page = 10, filters = {} }) {
        const client = createAdminApiClient();

        const response = await client.get(
            `${baseUrl(lista_id)}/inventario/vendidas`,
            { params: { page, per_page, ...filters } }
        );

        const data = response.data ?? {};
        const rawItems = data.data_indumentarias ?? data.data ?? data.items ?? [];
        const items = Array.isArray(rawItems) ? rawItems : [];
        const meta = {
            current_page: data.current_page ?? data.meta?.current_page ?? 1,
            last_page: data.last_page ?? data.meta?.last_page ?? 1,
            total: data.total ?? data.meta?.total ?? items.length,
            per_page: data.per_page ?? data.meta?.per_page ?? items.length,
        };

        return { items, meta };
    },

    /**
     * Obtiene el stock disponible por prenda y talla.
     * GET /listas-abonados/{lista_id}/bases/indumentarias/inventario/stock-disponible
     *
     * @param {{ lista_id: number|string, page?: number, per_page?: number,
     *   filters?: Object }} params
     * @returns {Promise<{ items: Array, meta: PaginationMeta }>}
     */
    async getInventarioStock({ lista_id, page = 1, per_page = 10, filters = {} }) {
        const client = createAdminApiClient();

        const response = await client.get(
            `${baseUrl(lista_id)}/inventario/stock-disponible`,
            { params: { page, per_page, ...filters } }
        );

        const data = response.data ?? {};
        const rawItems = data.data_indumentarias ?? data.data ?? data.items ?? [];
        const items = Array.isArray(rawItems) ? rawItems : [];
        const meta = {
            current_page: data.current_page ?? data.meta?.current_page ?? 1,
            last_page: data.last_page ?? data.meta?.last_page ?? 1,
            total: data.total ?? data.meta?.total ?? items.length,
            per_page: data.per_page ?? data.meta?.per_page ?? items.length,
        };

        return { items, meta };
    },
};
